import React from "react";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";

// MasterRoleStar — the marker that tells a LiftNet MASTER-organization role
// apart from a tenant's own roles, at a glance, wherever a role is listed or
// picked (roles tables, role selects, the create form's "Role Base" picker).
//
// It exists as ONE component precisely because the marker is expected to
// change: the icon, the color, the spacing and the tooltip copy all live here,
// so a redesign is a single edit instead of a hunt through every call site that
// happened to render its own <FontAwesomeIcon icon={faStar} …>.
//
// Pair it with isMasterOrgRole / isMasterOrgId from utils/masterOrganization —
// this component only draws the marker, it never decides who gets one.

/** Icon and color of the marker — change these to restyle every call site. */
export const MASTER_ROLE_STAR_ICON = faStar;
export const MASTER_ROLE_STAR_COLOR = "#fed03d";

interface MasterRoleStarProps {
  /**
   * Whether this role is a master-organization role. Rendering nothing when
   * false is the component's job, so call sites stay a plain
   * `<MasterRoleStar show={…} />` instead of a conditional around it.
   */
  show?: boolean;
  /** Tooltip text; defaults to the translated "LiftNet master role". */
  title?: string;
  /** Extra styles merged over the marker's own color/margin. */
  style?: React.CSSProperties;
}

function MasterRoleStar({ show = true, title = "", style = {} }: MasterRoleStarProps) {
  const [t] = useTranslation("global");

  if (!show) return null;

  return (
    <FontAwesomeIcon
      icon={MASTER_ROLE_STAR_ICON}
      style={{ color: MASTER_ROLE_STAR_COLOR, marginRight: 6, ...style }}
      title={title || t("organizationManagement.masterRoleTooltip")}
    />
  );
}

export default MasterRoleStar;
